import React, {
  Fragment,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { InfoWindowF, MarkerClusterer } from "@react-google-maps/api";
import {
  PropertyMarker,
  Marker,
  useCheckIfSubscriptionFeatureActiveBroker,
  ModalContext,
} from "dash-ui";

import { GetPropertyByIdQuery, Property } from "gql/generated.graphql";
import { RoutesPath } from "flow-components/routes/routes";

import PropertiesClusterInfo from "./components/PropertiesClusterInfo/PropertiesClusterInfo";

import {
  getClusteredMarkersRecord,
  propertyLocationToPosition,
} from "./MarkersCluster.helpers";
import { InfoWindowStyles } from "./MarkersCluster.styles";
import PropertyInfoCard from "./components/PropertyInfoCard/PropertyInfoCard";
import { isPropertyAccessFree } from "common/helpers";

interface IActiveClusterOptions {
  location: {
    lat: number;
    lng: number;
  };
  properties: GetPropertyByIdQuery["getPropertyById"][];
}
const minimumClusterSize = 10;
const MarkersCluster = ({
  properties,
  loading,
}: {
  properties: GetPropertyByIdQuery["getPropertyById"][];
  loading: boolean;
}) => {
  const { isSubscriptionActive } = useCheckIfSubscriptionFeatureActiveBroker();
  const { toggleModal } = useContext(ModalContext);

  const [activePropertyId, setActivePropertyId] = useState<null | string>(null);
  const [activeCluster, setActiveCluster] =
    useState<IActiveClusterOptions | null>(null);
  const [clusteredMarkers, setClusteredMarkers] = useState<
    Record<string, boolean>
  >({});

  const propertiesRecord: Record<
    string,
    GetPropertyByIdQuery["getPropertyById"]
  > = useMemo(
    () =>
      properties.reduce((obj, item) => {
        obj[item._id] = item;
        return obj;
      }, {} as Record<string, GetPropertyByIdQuery["getPropertyById"]>),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [properties]
  );

  const handleMarkerClick = (propertyId: string) => {
    if (isSubscriptionActive || isPropertyAccessFree(propertyId)) {
      window?.open(
        RoutesPath.PropertyPreview.replace(":id", propertyId),
        "_blank"
      );
      return;
    }
    toggleModal(true, "UpgradeSubscriptionModal");
  };

  const clusterHover = useCallback(
    (cluster: any) => {
      setActiveCluster({
        location: {
          lat: cluster.center?.lat() || 0,
          lng: cluster.center?.lng() || 0,
        },
        properties: cluster
          .getMarkers()
          .map((item: { title: string }) => propertiesRecord[item.title]),
      });
    },
    [propertiesRecord]
  );

  const onClusteringEnd = useCallback(
    (clustered: any) => {
      if (clustered.markers.length) {
        setClusteredMarkers(
          getClusteredMarkersRecord(clustered, minimumClusterSize)
        );
        setActiveCluster(null);
        setActivePropertyId(null);
      }
    },
    [setClusteredMarkers, setActiveCluster, setActivePropertyId]
  );
  /**
   * !!!important  Clusterer does not work with OverlayView
   * because of it we render two types of markers
   * <Marker /> with visible === false (to make Clusterer work)
   * and visible <PropertyMarker /> (which is OverlayView), to show not clustered markers
   */
  return (
    <>
      {!loading && (
        <MarkerClusterer
          minimumClusterSize={minimumClusterSize}
          onMouseOver={clusterHover}
          onMouseOut={() => setActiveCluster(null)}
          onClusteringEnd={onClusteringEnd}
        >
          {(clusterer) => (
            <>
              {properties.map((property) => (
                <Marker
                  visible={false}
                  key={property._id}
                  // use as id to link marker with property
                  title={property._id}
                  position={propertyLocationToPosition(property)}
                  clusterer={clusterer}
                />
              ))}
            </>
          )}
        </MarkerClusterer>
      )}
      {activeCluster && (
        <InfoWindowF
          position={activeCluster.location}
          options={{
            pixelOffset: new google.maps.Size(0, -10),
            disableAutoPan: true,
          }}
        >
          <PropertiesClusterInfo properties={activeCluster.properties} />
        </InfoWindowF>
      )}
      {properties.map((property) => (
        <Fragment key={property._id}>
          {clusteredMarkers[property._id] === false && (
            <PropertyMarker
              position={propertyLocationToPosition(property)}
              onMouseOnMarker={() => setActivePropertyId(property._id)}
              onMouseOutMarker={() => setActivePropertyId(null)}
              onClick={() => {
                handleMarkerClick(property._id);
              }}
              // @ts-ignore
              buildingTypes={property.buildingTypes || []}
            />
          )}
          {activePropertyId === property._id && (
            <InfoWindowF
              position={propertyLocationToPosition(property)}
              options={{
                pixelOffset: new google.maps.Size(0, -40),
                disableAutoPan: true,
              }}
            >
              <PropertyInfoCard
                key={property._id}
                property={property as Property}
                smallScale
              />
            </InfoWindowF>
          )}
        </Fragment>
      ))}
      <InfoWindowStyles />
    </>
  );
};

export default React.memo(MarkersCluster);
